import { formatCount } from '@gonfalon/format/formatCount';
import { formatNumber } from '@gonfalon/format/formatNumber';
import { msg } from '@gonfalon/messages/msg';
import { Alert } from '@launchpad-ui/components';

import { DEFAULT_USAGE_PRECISION } from '../../../../constants';
import type { DataExportBreakdown } from '../../../hooks/useDataExportBreakdown';
import { DataExportScope } from '../../../utils/combineDataExportUsage';
import type { PendingDestinationSource } from '../../../utils/dataExportBreakdown';
import { formatDateUTC } from '../../../utils/dates';

import { BaseSummaryCard } from './UsageSummaryCard';

import styles from './UsageSummaryCards.module.css';

type DataExportBreakdownCardsProps = {
  breakdown: DataExportBreakdown;
  percentChangeTooltip: string;
  precision?: number;
  streamingOnly?: boolean;
};

export function DataExportBreakdownCards({
  breakdown: { view, previous, pendingDestinations },
  percentChangeTooltip,
  precision = DEFAULT_USAGE_PRECISION,
  streamingOnly = false,
}: DataExportBreakdownCardsProps) {
  const change = { percentChangeTooltip, precision, isPreviousLoading: !previous };
  const readable = (title: string, total: number) => ({
    formattedTotal: formatCount(total),
    titleTooltip: msg('billing.dataExportCombined.cardValueTooltip', {
      title,
      total: formatNumber(total, { maximumFractionDigits: precision }),
    }),
  });

  const renderContent = () => {
    switch (view.status) {
      case 'error':
        return (
          <Alert status="error" className={styles.errorAlert}>
            {msg('billing.usageSummary.errorLoadingDimension')}
          </Alert>
        );
      case 'notYetAvailable':
        return <p className={styles.note}>{msg('billing.dataExportCombined.destinationNotYetAvailable')}</p>;
      default: {
        const isLoading = view.status === 'loading';
        const totalTitle = msg('billing.usageSummary.total');
        return (
          <>
            <div className={styles.summaryWithDimensions}>
              <BaseSummaryCard
                title={totalTitle}
                total={isLoading ? 0 : view.total}
                isTotalLoading={isLoading}
                previousTotal={previous?.total}
                {...change}
                {...(isLoading ? {} : readable(totalTitle, view.total))}
              />
              {view.status === 'ready' &&
                view.top.map(({ key, label, total }) => (
                  <BaseSummaryCard
                    key={key}
                    title={label}
                    total={total}
                    previousTotal={previous?.byKey.get(key)}
                    {...change}
                    {...readable(label, total)}
                  />
                ))}
            </div>
            {streamingOnly && <p className={styles.note}>{msg('billing.dataExportCombined.eventKindStreamingOnly')}</p>}
            {view.status === 'availableFrom' && (
              <p className={styles.note}>
                {msg('billing.dataExportCombined.destinationAvailableFrom', {
                  date: formatDateUTC(new Date(view.day)),
                })}
              </p>
            )}
            {view.status === 'ready' && pendingDestinations && (
              <p className={styles.note}>{getPendingDestinationsNote(pendingDestinations)}</p>
            )}
          </>
        );
      }
    }
  };

  return (
    <div className={styles.cardsContainer}>
      <div className={styles.title}>{msg('billing.dataExportCombined.breakdownTitle')}</div>
      {renderContent()}
    </div>
  );
}

function getPendingDestinationsNote({ source, day }: PendingDestinationSource) {
  const sourceLabel =
    source === DataExportScope.STREAMING
      ? msg('billing.dataExportCombined.streaming')
      : msg('billing.dataExportCombined.warehouse');
  return day === undefined
    ? msg('billing.dataExportCombined.sourceDestinationsNotYetAvailable', { source: sourceLabel })
    : msg('billing.dataExportCombined.sourceDestinationsAvailableFrom', {
        source: sourceLabel,
        date: formatDateUTC(new Date(day)),
      });
}
